import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import AnimateIn from "@/components/AnimateIn";
import TypewriterHeading from "@/components/TypewriterHeading";
import DropCard from "@/components/DropCard";
import EmailSignup from "@/components/EmailSignup";
import WCButton from "@/components/WCButton";
import { drops } from "@/data/mockData";

const TABS = [
  { id: "all", label: "ALL" },
  { id: "international", label: "INTERNATIONAL" },
  { id: "domestic", label: "DOMESTIC" },
  { id: "archive", label: "ARCHIVE" },
];

export default function Drops() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("tab") || "all";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const t = searchParams.get("tab") || "all";
    setTab(t);
  }, [searchParams]);

  const active = useMemo(() => drops.filter((d) => d.status !== "archived" && d.status !== "sold_out"), []);
  const archive = useMemo(() => drops.filter((d) => d.status === "archived" || d.status === "sold_out"), []);

  const filtered = useMemo(() => {
    if (tab === "all") return drops;
    if (tab === "archive") return archive;
    return drops.filter((d) => d.categoryTag === tab);
  }, [tab, archive]);

  return (
    <div data-testid="drops-page">
      {/* HERO */}
      <section className="bg-black pt-[160px] md:pt-[200px] pb-24 px-6 md:px-12" data-testid="drops-hero">
        <div className="max-w-5xl mx-auto">
          <TypewriterHeading
            text="WRONGCONCEPTION DROPS"
            className="text-5xl md:text-7xl lg:text-8xl tracking-widest"
            speed={60}
            testid="drops-typewriter"
          />
          <AnimateIn variant="fade" delay={2200}>
            <p className="text-white/75 text-base md:text-lg mt-10 max-w-3xl leading-relaxed">
              Every drop tells a story. Every story is a place. Every place is misunderstood.
              We travel. We shoot. We build. Once it is gone, it may not come back.
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* HOW DROPS WORK */}
      <section className="bg-black px-6 md:px-12 py-24" data-testid="how-drops-work">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-10 md:gap-6">
          {[
            { num: "01", title: "WE ANNOUNCE A LOCATION", body: "We tell you where we are going and why the world got it wrong." },
            { num: "02", title: "WE TRAVEL AND SHOOT", body: "Everything is documented on location. Real place. Real story. Real content." },
            { num: "03", title: "THE DROP LAUNCHES", body: "Pieces built from what we found. One window. Then it closes. No promise it returns." },
          ].map((s) => (
            <AnimateIn key={s.num} variant="fade" className="relative overflow-hidden py-12 px-6 border border-white/10">
              <div
                className="wc-heading absolute -top-10 -right-4 text-[12rem] md:text-[16rem] text-white/5 leading-none pointer-events-none"
                aria-hidden
              >
                {s.num}
              </div>
              <div className="relative z-10">
                <h3 className="wc-heading text-white text-3xl md:text-4xl tracking-widest">{s.title}</h3>
                <p className="text-white/70 mt-5 leading-relaxed">{s.body}</p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* FILTER TABS */}
      <section className="bg-black px-6 md:px-12 pt-8 pb-8 border-t border-white/10" data-testid="drops-tabs">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-8 md:gap-12">
          {TABS.map((t) => {
            const isActive = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => setSearchParams({ tab: t.id })}
                className={`wc-label text-xs md:text-sm pb-2 border-b-2 transition-colors ${
                  isActive ? "text-white border-white" : "text-white/40 border-transparent"
                }`}
                data-testid={`drops-tab-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* ACTIVE DROPS */}
      {tab !== "archive" && active.length > 0 && (
        <section className="bg-black px-6 md:px-12 py-20" data-testid="active-drops">
          <div className="max-w-7xl mx-auto">
            <AnimateIn variant="fade">
              <h2 className="wc-heading text-white text-4xl md:text-6xl tracking-widest">
                ACTIVE DROPS
              </h2>
            </AnimateIn>
            <div className="grid md:grid-cols-2 gap-10 md:gap-12 mt-14">
              {filtered
                .filter((d) => d.status !== "archived" && d.status !== "sold_out")
                .map((d, i) => (
                  <AnimateIn key={d.id} variant="fade" delay={i * 120}>
                    <DropCard drop={d} size="lg" />
                  </AnimateIn>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* ARCHIVE */}
      {(tab === "all" || tab === "archive") && (
        <section className="bg-black px-6 md:px-12 py-20 border-t border-white/10" data-testid="archive-drops">
          <div className="max-w-7xl mx-auto">
            <AnimateIn variant="fade">
              <h2 className="wc-heading text-white text-4xl md:text-6xl tracking-widest">
                THE ARCHIVE
              </h2>
              <p className="text-white/60 mt-4 text-base md:text-lg">
                Every drop that has come and gone. Real stories. Permanent history.
              </p>
            </AnimateIn>
            {archive.length === 0 ? (
              <p className="text-white/40 mt-10">No archived drops yet.</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-10 md:gap-12 mt-14">
                {archive.map((d, i) => (
                  <AnimateIn key={d.id} variant="fade" delay={i * 120}>
                    <DropCard drop={d} size="lg" />
                  </AnimateIn>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* WORLD IS NEXT */}
      <section className="bg-black px-6 md:px-12 py-32 border-t border-white/10" data-testid="world-next">
        <div className="max-w-5xl mx-auto text-center">
          <AnimateIn variant="fade">
            <h2 className="wc-heading text-white text-5xl md:text-7xl tracking-widest">
              THE WORLD IS NEXT
            </h2>
          </AnimateIn>
          <AnimateIn variant="fade" delay={200}>
            <p className="text-white/75 text-base md:text-lg mt-8 leading-relaxed">
              WrongConception is just getting started. Every country has a wrong conception the
              world has accepted without questioning. Every city has a story that was told wrong.
              We are going to all of them. International drops. Domestic drops. More wrong
              conceptions to challenge. More places to go. More stories to tell.
            </p>
          </AnimateIn>
          <AnimateIn variant="fade" delay={300}>
            <div className="mt-12">
              <WCButton as="a" href="#signup" data-testid="world-next-cta">
                Join The Waitlist
              </WCButton>
            </div>
          </AnimateIn>
        </div>
      </section>

      <div id="signup">
        <EmailSignup source="drops" testidPrefix="drops-signup" />
      </div>
    </div>
  );
}
